import PropTypes from "prop-types";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

/**
 * shadcn Dialog host for a form. Works in two modes: pass `open`/`onOpenChange`
 * to control it from the parent (e.g. an edit action inside a row menu), or omit
 * them to render a self-contained "New" trigger button (e.g. a table's create
 * button). The wrapped form receives `model`, `onSuccess`, and any `formProps`.
 */
const FormDialog = ({
  FormComponent,
  model,
  modelType,
  formProps,
  onSuccess,
  open,
  onOpenChange,
  triggerText,
  contentClassName,
  disablePointerDismissal,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const actualOpen = isControlled ? open : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;

  const header = model?.id ? `Edit ${modelType}` : `New ${modelType}`;

  const handleSuccess = () => {
    setOpen(false);
    onSuccess?.();
  };

  return (
    <Dialog
      open={actualOpen}
      onOpenChange={setOpen}
      disablePointerDismissal={disablePointerDismissal}
    >
      {!isControlled && (
        <DialogTrigger
          render={<Button size="sm">{triggerText || header}</Button>}
        />
      )}
      <DialogContent className={contentClassName}>
        <DialogTitle>{header}</DialogTitle>
        <FormComponent model={model} onSuccess={handleSuccess} {...formProps} />
      </DialogContent>
    </Dialog>
  );
};

FormDialog.propTypes = {
  FormComponent: PropTypes.elementType.isRequired,
  model: PropTypes.object,
  modelType: PropTypes.string,
  formProps: PropTypes.object,
  onSuccess: PropTypes.func,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  triggerText: PropTypes.string,
  contentClassName: PropTypes.string,
  disablePointerDismissal: PropTypes.bool,
};

export default FormDialog;
